import React, { useState } from "react";
import axios from "axios";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalBody,
} from "@chakra-ui/modal";
import { Button } from '@chakra-ui/react';
import { TokenManager } from "utils/tokenManager";

const ResendAccountConfirmModal: React.FC<{
    receiverAccountToResendConfirm: any;
    isOpen: boolean;
    onClose(): void;
    triggerReload(): void;
    setSwalProps: any;
}> = ({ isOpen, onClose, triggerReload, receiverAccountToResendConfirm, setSwalProps }) => {

    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    const closeModal = async () => {
        onClose();
    };

    const handleSubmit = async () => {
        try {
            setIsProcessing(true);

            const postData = {
                account_id: receiverAccountToResendConfirm.id,
                username: localStorage.getItem("username"),
                userRole: localStorage.getItem("userRole")?.toLowerCase()
            }

            await axios.post(
                process.env.REACT_APP_API_URL + `/api/${localStorage.getItem("userRole")?.toLowerCase()}/receiver/action/receiver-resend-account-confirm`,
                postData
            )
                .then(function (response) {
                    if (response && response.data?.status === 'ok') {
                        onClose();
                        setSwalProps({
                            show: true,
                            icon: "success",
                            title: "Success",
                            html: "Account confirmation has been successfully resent",
                            showConfirmButton: true,
                            didClose: () => {
                                setSwalProps({});
                                setIsProcessing(false);
                            },
                        });
                    } else {
                        setIsProcessing(false);
                    }
                })
                .catch(function (error) {
                    setIsProcessing(false);
                    setSwalProps({
                        show: true,
                        icon: "error",
                        title: "Oops!",
                        html: error.response.data.message,
                        showConfirmButton: true,
                        didClose: () => {
                            setSwalProps({});
                            setIsProcessing(false);
                        },
                    });
                });
        } catch (error: any) {
            setIsProcessing(false);
            setSwalProps({
                show: true,
                icon: "error",
                title: "Oops!",
                html: "Something went wrong, please try again later.",
                showConfirmButton: true,
                didClose: () => {
                    setSwalProps({});
                    setIsProcessing(false);
                },
            });
        }
    };

    const fieldsRendering = (fields: Record<string, any>) => {
        const renderInput = (fieldName: string, fieldValue: any) => {
            const capitalizeLabel = (label: string) => {
                let modifiedLabel = label
                    .split('_')
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');

                switch (label) {
                    case "bsb":
                        modifiedLabel = "BSB";
                        break;
                    case "bic":
                        modifiedLabel = "BIC";
                        break;
                    case "iban":
                        modifiedLabel = "IBAN";
                        break;
                }

                return modifiedLabel;
            };

            return (
                <div className="flex" key={fieldName}>
                    <dt className="w-28 shrink-0 text-gray-700 dark:text-gray-400">
                        {capitalizeLabel(fieldName)}
                    </dt>
                    <dd className="font-medium text-gray-900 dark:text-white">
                        {fieldValue}
                    </dd>
                </div>
            );
        };

        return Object.entries(fields).map(([key, value]) => renderInput(key, value));
    };

    return (
        <>
            {receiverAccountToResendConfirm &&
                <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
                    <ModalOverlay className="bg-[#000] !opacity-30" />
                    <ModalContent className="dark:!bg-neutral-900 !z-[1002] !m-auto !w-full max-w-xl !rounded-2xl !border !border-gray-200 !bg-white !shadow-xl dark:!border-white/10 md:!top-[12vh]">
                        <ModalBody className="p-6 md:p-7">
                            <div className="mb-4 flex items-start gap-3">
                                <div className="rounded-xl bg-primary-50 p-2 text-primary-600 ring-1 ring-primary-100 dark:bg-primary-900/20 dark:text-primary-400 dark:ring-primary-900/30">
                                    <svg
                                        viewBox="0 0 24 24"
                                        className="h-5 w-5"
                                        fill="currentColor"
                                    >
                                        <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                                    </svg>
                                </div>
                                <div>
                                    <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                                        Resend Confirmation Link
                                    </h1>
                                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                                        Send verification link to the receiver again
                                    </p>
                                </div>
                            </div>

                            <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 dark:border-blue-900/40 dark:bg-blue-900/20 dark:text-blue-300">
                                You're about to resend the link to the receiver for account verification. Please confirm to proceed.
                            </div>

                            <div className="dark:bg-neutral-800/50 mb-6 rounded-xl bg-gray-50 p-4 ring-1 ring-gray-100 dark:ring-white/10">
                                <dl className="flex flex-col gap-3 text-sm">
                                    <div className="flex">
                                        <dt className="w-28 shrink-0 text-gray-700 dark:text-gray-400">
                                            Asset
                                        </dt>
                                        <dd className="font-medium text-gray-900 dark:text-white">
                                            {receiverAccountToResendConfirm.asset}
                                        </dd>
                                    </div>

                                    <div className="flex">
                                        <dt className="w-28 shrink-0 text-gray-700 dark:text-gray-400">
                                            Method
                                        </dt>
                                        <dd className="font-medium text-gray-900 dark:text-white">
                                            {receiverAccountToResendConfirm.method_name}
                                        </dd>
                                    </div>

                                    {fieldsRendering(receiverAccountToResendConfirm.method_details)}
                                </dl>
                            </div>

                            <div className="flex justify-end gap-2">
                                <Button
                                    onClick={() => closeModal()}
                                    isDisabled={isProcessing}
                                    className="dark:bg-neutral-900 dark:hover:bg-neutral-800 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-white/10 dark:text-white"
                                >
                                    Not now
                                </Button>

                                <Button
                                    onClick={() => handleSubmit()}
                                    isDisabled={isProcessing || TokenManager.isImpersonating()}
                                    isLoading={isProcessing}
                                    loadingText='Sending...'
                                    className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 disabled:cursor-not-allowed disabled:bg-gray-400"
                                >
                                    Resend confirmation link
                                </Button>
                            </div>
                        </ModalBody>
                    </ModalContent>
                </Modal>
            }
        </>
    );
};

export default ResendAccountConfirmModal;